<template>
  <div class="backtest-data-table">
    <div
      class="table-wrapper"
      v-loading="loading"
      element-loading-text="Loading table data..."
      element-loading-background="rgba(255, 255, 255, 0.9)"
    >
      <!-- Table -->
      <div class="table-scroll">
        <table class="data-table">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column">
                <div class="header-cell">
                  <div
                    class="header-title"
                    :class="{ sortable: isSortableColumn(column) }"
                    @click="handleSortClick(column)"
                  >
                    <span>{{ column }}</span>
                    <span
                      v-if="isSortableColumn(column) && sortColumn === column"
                      class="sort-icon"
                    >
                      {{ sortOrder === 'asc' ? '▲' : '▼' }}
                    </span>
                  </div>
                  <div
                    v-if="isFilterableColumn(column)"
                    class="filter-icon-wrapper"
                  >
                    <button
                      class="filter-icon-btn"
                      :class="{
                        active: activeFilterColumn === column,
                        filtered: hasFilterValue(column)
                      }"
                      @click.stop="toggleFilterDropdown(column)"
                      title="Filter"
                    >
                      <svg
                        class="filter-icon"
                        viewBox="0 0 16 16"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.5 2h13l-5 6v5.5l-3 1.5V8l-5-6z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <div
                      v-if="activeFilterColumn === column"
                      class="filter-dropdown"
                      @click.stop
                    >
                      <el-select
                        v-if="isSelectColumn(column)"
                        v-model="columnFilters[column]"
                        placeholder="All"
                        clearable
                        size="small"
                        @change="applyFilters"
                      >
                        <el-option
                          v-for="value in getColumnValues(column)"
                          :key="value"
                          :label="value"
                          :value="value"
                        >
                        </el-option>
                      </el-select>
                      <el-input
                        v-else
                        v-model="columnFilters[column]"
                        placeholder="Filter..."
                        clearable
                        size="small"
                        @input="applyFilters"
                      >
                      </el-input>
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedData.length === 0" class="no-data-row">
              <td :colspan="columns.length" class="no-data-cell">
                <div class="no-data-content">
                  <i class="el-icon-document"></i>
                  <span>No data available</span>
                </div>
              </td>
            </tr>
            <tr
              v-else
              v-for="(row, index) in paginatedData"
              :key="index"
              class="data-row"
            >
              <td
                v-for="column in columns"
                :key="`${index}-${column}`"
                :title="row[column]"
              >
                {{ row[column] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <el-pagination
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :total="filteredData.length"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        >
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BackTestDataTable',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array,
      default: () => []
    },
    pageSize: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      currentPage: 1,
      selectFilterColumns: [
        'connectionstatus',
        'podmac',
        'podstatus',
        'position',
        'nodemac',
        'hwversion',
        'firmwareversion',
        'contractorid'
      ],
      sortableColumns: ['timestamp', 'localtimestamp'],
      columnFilters: {},
      activeFilterColumn: null,
      sortColumn: null,
      sortOrder: 'asc'
    }
  },
  computed: {
    columns() {
      if (!this.data || this.data.length === 0) return []
      return Object.keys(this.data[0])
    },
    filteredData() {
      let result = this.data

      // Apply global filters first
      if (Array.isArray(this.filters) && this.filters.length > 0) {
        result = result.filter(row => {
          return this.filters.every(filter => {
            if (!filter.column || !filter.value) return true

            const cellValue = row[filter.column]
            if (cellValue === null || cellValue === undefined) return true

            // Try to parse as number for numeric columns
            const numValue = parseFloat(cellValue)
            const filterNum = parseFloat(filter.value)

            if (!isNaN(numValue) && !isNaN(filterNum)) {
              // Numeric comparison
              switch (filter.operator) {
                case '=':
                  return numValue === filterNum
                case '>':
                  return numValue > filterNum
                case '>=':
                  return numValue >= filterNum
                case '<':
                  return numValue < filterNum
                case '<=':
                  return numValue <= filterNum
                default:
                  return true
              }
            } else {
              // String comparison
              return String(cellValue)
                .toLowerCase()
                .includes(filter.value.toLowerCase())
            }
          })
        })
      }

      // Apply column-specific filters (from table header)
      for (const column in this.columnFilters) {
        const filterValue = this.columnFilters[column]
        if (filterValue && typeof filterValue === 'string') {
          if (this.isSelectColumn(column)) {
            // Selection: Strict match
            result = result.filter(row => String(row[column]) === filterValue)
          } else {
            // Input: Fuzzy match
            result = result.filter(row => {
              const cellValue = String(row[column] || '').toLowerCase()
              return cellValue.includes(filterValue.toLowerCase())
            })
          }
        }
      }

      if (this.sortColumn && this.isSortableColumn(this.sortColumn)) {
        const direction = this.sortOrder === 'asc' ? 1 : -1
        result = [...result].sort((a, b) => {
          const aVal = this.getSortValue(a[this.sortColumn])
          const bVal = this.getSortValue(b[this.sortColumn])
          if (aVal < bVal) return -1 * direction
          if (aVal > bVal) return 1 * direction
          return 0
        })
      }

      return result
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredData.slice(start, end)
    }
  },
  watch: {
    data() {
      this.currentPage = 1
      this.initializeFilters()
    },
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.initializeFilters()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    normalizeColumn(column) {
      return String(column || '').toLowerCase()
    },
    initializeFilters() {
      if (!this.data || this.data.length === 0) return
      const filters = {}
      this.columns.forEach(column => {
        filters[column] = ''
      })
      this.columnFilters = filters
    },
    isFilterableColumn(column) {
      return !this.isSortableColumn(column)
    },
    isSelectColumn(column) {
      return this.selectFilterColumns.includes(this.normalizeColumn(column))
    },
    isSortableColumn(column) {
      return this.sortableColumns.includes(this.normalizeColumn(column))
    },
    getColumnValues(column) {
      const values = new Set(this.data.map(row => row[column]))
      return Array.from(values)
        .filter(v => v !== null && v !== undefined)
        .sort()
        .map(v => String(v))
    },
    applyFilters() {
      this.currentPage = 1
    },
    handlePageChange(page) {
      this.currentPage = page
    },
    handleSortClick(column) {
      if (!this.isSortableColumn(column)) return
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
    },
    getSortValue(value) {
      if (value === null || value === undefined) return ''
      const num = Number(value)
      if (!Number.isNaN(num) && String(value).trim() !== '') return num
      const dateValue = Date.parse(value)
      if (!Number.isNaN(dateValue)) return dateValue
      return String(value).toLowerCase()
    },
    toggleFilterDropdown(column) {
      if (this.activeFilterColumn === column) {
        this.activeFilterColumn = null
      } else {
        this.activeFilterColumn = column
      }
    },
    handleClickOutside(event) {
      const filterWrapper = event.target.closest('.filter-icon-wrapper')
      if (!filterWrapper && this.activeFilterColumn) {
        this.activeFilterColumn = null
      }
    },
    hasFilterValue(column) {
      return this.columnFilters[column] && this.columnFilters[column] !== ''
    }
  }
}
</script>

<style scoped lang="scss">
.backtest-data-table {
  .no-data {
    padding: 30px;
    text-align: center;
    color: #666;
    font-size: 14px;
  }

  .table-wrapper {
    display: flex;
    flex-direction: column;
    gap: 15px;

    // Table
    .table-scroll {
      overflow-x: auto;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      max-height: 600px;
      overflow-y: auto;

      .data-table {
        width: 100%;
        border-collapse: collapse;
        background-color: #fff;
        font-size: 13px;

        thead {
          position: sticky;
          top: 0;
          z-index: 10;
          background-color: #f5f7fa;

          th {
            padding: 0;
            text-align: left;
            border-bottom: 1px solid #ebeef5;
            white-space: nowrap;
            position: relative;

            .header-cell {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 12px;
              gap: 8px;

              .header-title {
                display: flex;
                align-items: center;
                gap: 6px;
                user-select: none;
                font-weight: 600;
                flex: 1;

                &.sortable {
                  cursor: pointer;
                }

                .sort-icon {
                  font-size: 10px;
                  color: #409eff;
                }
              }

              .filter-icon-wrapper {
                position: relative;
                display: flex;
                flex-shrink: 0;

                .filter-icon-btn {
                  padding: 4px 6px;
                  background-color: transparent;
                  border: 1px solid #dcdfe6;
                  border-radius: 3px;
                  color: #666;
                  font-size: 14px;
                  cursor: pointer;
                  transition: all 0.2s;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 28px;
                  height: 28px;
                  line-height: 1;

                  .filter-icon {
                    width: 14px;
                    height: 14px;
                  }

                  &:hover {
                    border-color: #409eff;
                    color: #409eff;
                  }

                  &.active {
                    background-color: #409eff;
                    border-color: #409eff;
                    color: #fff;
                  }

                  &.filtered:not(.active) {
                    background-color: #ecf5ff;
                    border-color: #409eff;
                    color: #409eff;
                  }
                }

                .filter-dropdown {
                  position: absolute;
                  top: 100%;
                  left: 0;
                  background-color: #fff;
                  border: 1px solid #dcdfe6;
                  border-radius: 4px;
                  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
                  z-index: 1000;
                  min-width: 150px;
                  margin-top: 4px;
                  padding: 8px;

                  ::v-deep .el-select {
                    width: 100%;
                  }
                }
              }
            }
          }
        }

        tbody {
          tr.no-data-row {
            .no-data-cell {
              padding: 40px 20px;
              text-align: center;
              border: none;

              .no-data-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 10px;
                color: #909399;
                font-size: 14px;

                i {
                  font-size: 48px;
                  color: #c0c4cc;
                }
              }
            }
          }

          tr.data-row {
            border-bottom: 1px solid #f0f0f0;
            transition: background-color 0.2s;

            &:hover {
              background-color: #f5f7fa;
            }

            &:nth-child(even) {
              background-color: #fafafa;

              &:hover {
                background-color: #f5f7fa;
              }
            }

            td {
              padding: 10px 12px;
              border-right: 1px solid #f0f0f0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 300px;

              &:last-child {
                border-right: none;
              }
            }
          }
        }
      }
    }

    // Pagination
    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 15px 0;
      border-top: 1px solid #ebeef5;
    }
  }
}
</style>
